import React, { useState } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Image,
  ActivityIndicator,
} from "react-native";
import { useForm, Controller } from "react-hook-form";
import { Feather } from "@expo/vector-icons";
import { router } from "expo-router";
import { Toast } from "react-native-toast-notifications";
import { useDispatch } from "react-redux";
import i18n, { availableLocales } from "../../locales/i18n";
import customAxios from "../../utils/customAxios";
import { appActions } from "../../store/slices/appSlice";

// Import images
import ForgotImage from "../../assets/images/sell.png";

const WebForgetPasswordScreen: React.FC = () => {
  const locale = i18n.locale;
  const dispatch = useDispatch();
  const [showLanguages, setShowLanguages] = useState(false);
  const [buttonSpinner, setButtonSpinner] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState(1); // 1: Enter email, 2: Enter code and new password

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      email: "",
      verificationCode: "",
      newPassword: "",
    },
  });

  const handleSendCode = async (data: any) => {
    setButtonSpinner(true);
    try {
      const response = await customAxios.post("/users/forgot-password/", {
        email: data.email,
      });

      Toast.show(response.data?.message || i18n.t("verificationCodeSent"), {
        type: "success",
      });

      setStep(2);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        i18n.t("errorOccurred");

      Toast.show(errorMessage, { type: "danger" });
    } finally {
      setButtonSpinner(false);
    }
  };

  const handleResetPassword = async (data: any) => {
    setButtonSpinner(true);
    try {
      const response = await customAxios.post("/users/reset-forgot-password", {
        verificationCode: data.verificationCode,
        email: data.email,
        newpassword: data.newPassword,
      });

      Toast.show(response.data?.message || i18n.t("passwordResetSuccess"), {
        type: "success",
      });

      router.replace("/(routes)/(auth)/login");
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        i18n.t("errorOccurred");

      Toast.show(errorMessage, { type: "danger" });
    } finally {
      setButtonSpinner(false);
    }
  };

  const handleLanguageChange = async (languageCode: string) => {
    setShowLanguages(false);
    dispatch(appActions.setLocale(languageCode));
  };

  return (
    <View style={styles.container}>
      {/* Left Panel - Branding */}
      <View style={styles.leftPanel}>
        <View style={styles.brandingContent}>
          <Image
            source={require("../../assets/logo-no-bg-white.png")}
            style={styles.logo}
            resizeMode="contain"
          />
          <Text style={styles.brandTitle}>خدام</Text>
          <Text style={styles.brandSubtitle}>
            {i18n.t("welcomeIntoDesc1")}
          </Text>
          <Image
            source={ForgotImage}
            style={styles.illustrationImage}
            resizeMode="contain"
          />
        </View>
      </View>

      {/* Right Panel - Form */}
      <View style={styles.rightPanel}>
        <View style={styles.formContainer}>
          {/* Language Selector */}
          <View style={styles.languageSelector}>
            <TouchableOpacity
              style={styles.languageButton}
              onPress={() => setShowLanguages(!showLanguages)}
            >
              <Text style={styles.languageButtonText}>🌐</Text>
              <Text style={styles.languageButtonLabel}>
                {availableLocales.find((l) => l.code === locale)?.name || locale}
              </Text>
            </TouchableOpacity>

            {showLanguages && (
              <View style={styles.languageDropdown}>
                {availableLocales.map((lang) => (
                  <TouchableOpacity
                    key={lang.code}
                    style={[
                      styles.languageOption,
                      locale === lang.code && styles.languageOptionActive,
                    ]}
                    onPress={() => handleLanguageChange(lang.code)}
                  >
                    <Text
                      style={[
                        styles.languageOptionText,
                        locale === lang.code && styles.languageOptionTextActive,
                      ]}
                    >
                      {lang.name}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            )}
          </View>

          {/* Step Indicator */}
          <View style={styles.stepIndicator}>
            <View style={[styles.stepDot, step >= 1 && styles.stepDotActive]} />
            <View style={styles.stepLine} />
            <View style={[styles.stepDot, step >= 2 && styles.stepDotActive]} />
          </View>

          {/* Header */}
          <View style={styles.formHeader}>
            <Text style={styles.formTitle}>
              {step === 1 ? i18n.t("forgotPassword") : i18n.t("resetPassword")}
            </Text>
            <Text style={styles.formSubtitle}>
              {step === 1
                ? i18n.t("enterEmailToResetPassword")
                : i18n.t("enterCodeAndNewPassword")}
            </Text>
          </View>

          {/* Email Field */}
          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>{i18n.t("email")}</Text>
            <View style={[styles.inputWrapper, step === 2 && styles.inputDisabled]}>
              <Feather name="mail" size={20} color="#999" style={styles.inputIcon} />
              <Controller
                control={control}
                name="email"
                rules={{
                  required: i18n.t("mailRequired"),
                  pattern: { value: /^\S+@\S+$/i, message: i18n.t("invalidEmail") },
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <TextInput
                    style={styles.textInput}
                    placeholder={i18n.t("enterEmail")}
                    placeholderTextColor="#999"
                    autoCapitalize="none"
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    keyboardType="email-address"
                    editable={step === 1}
                  />
                )}
              />
            </View>
            {errors.email && (
              <Text style={styles.errorText}>{errors.email.message}</Text>
            )}
          </View>

          {/* Step 2 Fields */}
          {step === 2 && (
            <>
              {/* Verification Code Field */}
              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>{i18n.t("enterVerificationCode")}</Text>
                <View style={styles.inputWrapper}>
                  <Feather name="key" size={20} color="#999" style={styles.inputIcon} />
                  <Controller
                    control={control}
                    name="verificationCode"
                    rules={{ required: i18n.t("enterVerificationCode") }}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <TextInput
                        style={styles.textInput}
                        placeholder="123456"
                        placeholderTextColor="#999"
                        value={value}
                        onChangeText={onChange}
                        onBlur={onBlur}
                        keyboardType="number-pad"
                        maxLength={6}
                      />
                    )}
                  />
                </View>
                {errors.verificationCode && (
                  <Text style={styles.errorText}>{errors.verificationCode.message}</Text>
                )}
              </View>

              {/* New Password Field */}
              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>{i18n.t("newPassword")}</Text>
                <View style={styles.inputWrapper}>
                  <Feather name="lock" size={20} color="#999" style={styles.inputIcon} />
                  <Controller
                    control={control}
                    name="newPassword"
                    rules={{
                      required: i18n.t("passwordRequired"),
                      minLength: { value: 5, message: i18n.t("passwordMinLength") },
                    }}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <TextInput
                        style={styles.textInput}
                        placeholder="••••••••"
                        placeholderTextColor="#999"
                        autoCapitalize="none"
                        value={value}
                        onChangeText={onChange}
                        onBlur={onBlur}
                        secureTextEntry={!showPassword}
                      />
                    )}
                  />
                  <TouchableOpacity onPress={() => setShowPassword(!showPassword)}>
                    <Feather
                      name={showPassword ? "eye" : "eye-off"}
                      size={20}
                      color="#999"
                    />
                  </TouchableOpacity>
                </View>
                {errors.newPassword && (
                  <Text style={styles.errorText}>{errors.newPassword.message}</Text>
                )}
              </View>
            </>
          )}

          {/* Action Button */}
          <TouchableOpacity
            style={styles.actionButton}
            onPress={handleSubmit(step === 1 ? handleSendCode : handleResetPassword)}
            disabled={buttonSpinner}
          >
            {buttonSpinner ? (
              <ActivityIndicator size="small" color="#FFFFFF" />
            ) : (
              <Text style={styles.actionButtonText}>
                {step === 1 ? i18n.t("sendCode") : i18n.t("resetPassword")}
              </Text>
            )}
          </TouchableOpacity>

          {/* Back to previous step - Only show in step 2 */}
          {step === 2 && (
            <TouchableOpacity
              style={styles.backStepButton}
              onPress={() => setStep(1)}
            >
              <Text style={styles.backStepButtonText}>
                {i18n.t("backToPreviousStep")}
              </Text>
            </TouchableOpacity>
          )}

          {/* Links */}
          <View style={styles.linksContainer}>
            <TouchableOpacity onPress={() => router.push("/(routes)/(auth)/login")}>
              <Text style={styles.linkText}>
                {i18n.t("backToLogin")}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => router.push("/(routes)/(auth)/signup")}>
              <Text style={styles.secondaryLinkText}>
                {i18n.t("donthaveaccount")}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: "row",
    minHeight: "100vh" as any,
    backgroundColor: "#F8F9FA",
  },
  leftPanel: {
    flex: 1,
    backgroundColor: "#004E6D",
    justifyContent: "center",
    alignItems: "center",
    padding: 40,
  },
  brandingContent: {
    alignItems: "center",
    maxWidth: 400,
  },
  logo: {
    width: 80,
    height: 80,
    marginBottom: 24,
  },
  brandTitle: {
    fontSize: 56,
    fontWeight: "bold",
    color: "#FFFFFF",
    marginBottom: 16,
  },
  brandSubtitle: {
    fontSize: 20,
    color: "rgba(255, 255, 255, 0.9)",
    textAlign: "center",
    lineHeight: 28,
    marginBottom: 40,
  },
  illustrationImage: {
    width: 300,
    height: 250,
  },
  rightPanel: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 40,
    backgroundColor: "#FFFFFF",
  },
  formContainer: {
    width: "100%",
    maxWidth: 420,
  },
  languageSelector: {
    position: "absolute" as any,
    top: -60,
    right: 0,
    zIndex: 100,
  },
  languageButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F0F0F0",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    gap: 8,
  },
  languageButtonText: {
    fontSize: 20,
  },
  languageButtonLabel: {
    fontSize: 16,
    color: "#333",
    fontWeight: "500",
  },
  languageDropdown: {
    position: "absolute" as any,
    top: 44,
    right: 0,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 5,
    minWidth: 140,
    overflow: "hidden",
    zIndex: 9999,
  },
  languageOption: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#F0F0F0",
  },
  languageOptionActive: {
    backgroundColor: "#E8F4F8",
  },
  languageOptionText: {
    fontSize: 16,
    color: "#333",
  },
  languageOptionTextActive: {
    color: "#004E6D",
    fontWeight: "600",
  },
  stepIndicator: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 32,
  },
  stepDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: "#E0E0E0",
  },
  stepDotActive: {
    backgroundColor: "#004E6D",
  },
  stepLine: {
    width: 60,
    height: 2,
    backgroundColor: "#E0E0E0",
    marginHorizontal: 8,
  },
  formHeader: {
    marginBottom: 32,
  },
  formTitle: {
    fontSize: 36,
    fontWeight: "bold",
    color: "#004E6D",
    marginBottom: 8,
    textAlign: "center",
  },
  formSubtitle: {
    fontSize: 18,
    color: "#666",
    textAlign: "center",
    lineHeight: 24,
  },
  inputGroup: {
    marginBottom: 20,
  },
  inputLabel: {
    fontSize: 16,
    fontWeight: "500",
    color: "#333",
    marginBottom: 8,
    textAlign: "right",
  },
  inputWrapper: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F8F9FA",
    borderWidth: 1,
    borderColor: "#E0E0E0",
    borderRadius: 12,
    paddingHorizontal: 16,
    height: 52,
  },
  inputDisabled: {
    backgroundColor: "#EBEBEB",
  },
  inputIcon: {
    marginRight: 12,
  },
  textInput: {
    flex: 1,
    fontSize: 18,
    color: "#333",
    textAlign: "right",
    outlineStyle: "none" as any,
  },
  errorText: {
    color: "#E53935",
    fontSize: 14,
    marginTop: 4,
    textAlign: "right",
  },
  actionButton: {
    backgroundColor: "#004E6D",
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: "center",
    marginBottom: 16,
    shadowColor: "#004E6D",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 4,
  },
  actionButtonText: {
    color: "#FFFFFF",
    fontSize: 20,
    fontWeight: "600",
  },
  backStepButton: {
    alignItems: "center",
    marginBottom: 16,
  },
  backStepButtonText: {
    color: "#0596D0",
    fontSize: 16,
    fontWeight: "500",
  },
  linksContainer: {
    alignItems: "center",
    gap: 12,
  },
  linkText: {
    fontSize: 16,
    color: "#004E6D",
    fontWeight: "600",
  },
  secondaryLinkText: {
    fontSize: 16,
    color: "#666",
  },
});

export default WebForgetPasswordScreen;
